import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Send, Phone, Mail, MapPin, Clock, Loader2, CheckCircle2, AlertCircle, BadgePercent } from 'lucide-react';
import { company, services } from '../data/content';
import { PhotoUpload } from './PhotoUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = { name: '', company: '', phone: '', email: '', location: '', service: '', message: '', promo_code: '', consent: false };

export default function ContactForm() {
  const [form, setForm] = useState(() => ({ ...emptyForm, promo_code: localStorage.getItem('hh_promo_code') || '' }));
  const [photos, setPhotos] = useState([]);
  const [status, setStatus] = useState({ state: 'idle', msg: '' });

  useEffect(() => {
    const onPromo = (e) => setForm((f) => ({ ...f, promo_code: e.detail }));
    window.addEventListener('hh-promo-code', onPromo);
    return () => window.removeEventListener('hh-promo-code', onPromo);
  }, []);

  const handle = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const patchPhoto = (key, patch) => setPhotos((ps) => ps.map((p) => (p.key === key ? { ...p, ...patch } : p)));

  const uploadPhotos = async () => {
    const paths = [];
    for (const p of photos) {
      if (p.status === 'done' && p.path) { paths.push(p.path); continue; }
      patchPhoto(p.key, { status: 'uploading', progress: 0 });
      const fd = new FormData();
      fd.append('file', p.file, p.file.name);
      try {
        const res = await axios.post(`${API}/upload`, fd, {
          onUploadProgress: (ev) => patchPhoto(p.key, { progress: ev.total ? Math.round((ev.loaded / ev.total) * 100) : 0 }),
        });
        patchPhoto(p.key, { status: 'done', progress: 100, path: res.data.path });
        paths.push(res.data.path);
      } catch (err) {
        patchPhoto(p.key, { status: 'error' });
        throw new Error('upload');
      }
    }
    return paths;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      setStatus({ state: 'error', msg: 'Bitte stimmen Sie der Datenschutzerklärung zu.' });
      return;
    }
    setStatus({ state: 'loading', msg: '' });
    try {
      const photoPaths = await uploadPhotos();
      await axios.post(`${API}/contact`, {
        name: form.name, company: form.company, phone: form.phone, email: form.email,
        location: form.location, service: form.service, message: form.message, photos: photoPaths, promo_code: form.promo_code.trim().toUpperCase(),
      });
      localStorage.removeItem('hh_promo_code');
      setStatus({ state: 'success', msg: `Vielen Dank! Wir melden uns zeitnah bei Ihnen zurück.${photoPaths.length ? ` ${photoPaths.length} Foto(s) wurden übermittelt.` : ''}` });
      photos.forEach((p) => URL.revokeObjectURL(p.preview));
      setPhotos([]);
      setForm(emptyForm);
    } catch (err) {
      const isUpload = err?.message === 'upload';
      setStatus({ state: 'error', msg: isUpload ? 'Ein Foto konnte nicht hochgeladen werden. Bitte entfernen Sie es oder versuchen Sie es erneut.' : 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut oder rufen Sie uns an.' });
    }
  };

  return (
    <section id="kontakt" className="py-20 md:py-28 bg-[#F4EFE7] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2">
          <span className="crown-divider text-[#B8892A]">Kontakt</span>
          <h2 className="mt-4 text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#0B1E44] leading-tight">
            Jetzt unverbindlich anfragen.
          </h2>
          <p className="mt-5 text-slate-600 text-lg leading-relaxed">
            Pflaster, Einfahrt, Terrasse, Fassade, Dach oder Innenräume – senden Sie eine kurze Anfrage, wir melden uns zeitnah zurück.
          </p>

          <div className="mt-8 space-y-4">
            {[
              { i: Phone, l: 'Telefon', v: company.phone, href: `tel:${company.phoneRaw}` },
              { i: Mail, l: 'E-Mail', v: company.email, href: `mailto:${company.email}` },
              { i: MapPin, l: 'Adresse', v: `${company.address.street}, ${company.address.zip} ${company.address.city}`, href: `https://maps.google.com/?q=${encodeURIComponent(company.address.street + ', ' + company.address.zip + ' ' + company.address.city)}` },
            ].map((c, i) => (
              <a key={i} href={c.href} target={c.i === MapPin ? '_blank' : undefined} rel="noreferrer" className="flex items-start gap-4 bg-white rounded-xl p-4 border border-slate-100 card-shadow hover:border-[#E0A72C]/60 transition-colors">
                <div className="w-11 h-11 rounded-lg bg-[#0B1E44] text-white flex items-center justify-center shrink-0">
                  <c.i className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{c.l}</div>
                  <div className="font-semibold text-[#0B1E44] mt-0.5">{c.v}</div>
                </div>
              </a>
            ))}

            <div className="flex items-start gap-4 bg-white rounded-xl p-4 border border-slate-100 card-shadow">
              <div className="w-11 h-11 rounded-lg bg-[#0B1E44] text-white flex items-center justify-center shrink-0">
                <Clock className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Öffnungszeiten</div>
                <ul className="mt-1.5 space-y-0.5 text-sm">
                  {company.hours.map((h, i) => (
                    <li key={i} className="flex justify-between gap-4 text-[#0B1E44]">
                      <span className="font-medium">{h.d}</span>
                      <span className="text-slate-600">{h.t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="lg:col-span-3 bg-white rounded-2xl p-6 sm:p-8 card-shadow border border-slate-100">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Name *" name="name" value={form.name} onChange={handle} required />
            <Field label="Firma" name="company" value={form.company} onChange={handle} />
            <Field label="Telefon" name="phone" value={form.phone} onChange={handle} type="tel" />
            <Field label="E-Mail *" name="email" value={form.email} onChange={handle} type="email" required />
            <div className="sm:col-span-2">
              <Field label="Ort / Objektadresse" name="location" value={form.location} onChange={handle} />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs uppercase tracking-wider font-semibold text-slate-600 mb-1.5">Gewünschte Leistung</label>
              <select name="service" value={form.service} onChange={handle} className="w-full h-12 px-4 rounded-lg bg-[#FAF7F2] border border-slate-200 focus:border-[#B8892A] focus:ring-2 focus:ring-[#E0A72C]/30 outline-none transition text-[#0B1E44]">
                <option value="">Bitte wählen …</option>
                {services.map((s) => <option key={s.id} value={s.title}>{s.title}</option>)}
                <option value="Mehrere Leistungen">Mehrere Leistungen</option>
                <option value="Ich bin unsicher">Ich bin unsicher</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs uppercase tracking-wider font-semibold text-slate-600 mb-1.5">Nachricht</label>
              <textarea name="message" value={form.message} onChange={handle} rows={5} className="w-full px-4 py-3 rounded-lg bg-[#FAF7F2] border border-slate-200 focus:border-[#B8892A] focus:ring-2 focus:ring-[#E0A72C]/30 outline-none transition text-[#0B1E44]" placeholder="Kurz beschreiben, was gemacht werden soll …" data-testid="contact-message" />
            </div>
            <div className="sm:col-span-2">
              <PhotoUpload photos={photos} onChange={setPhotos} disabled={status.state === 'loading'} />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs uppercase tracking-wider font-semibold text-slate-600 mb-1.5">Rabattcode <span className="text-slate-400 font-normal normal-case tracking-normal">(optional)</span></label>
              <div className="relative">
                <BadgePercent className={`absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 ${form.promo_code ? 'text-[#DC2626]' : 'text-slate-400'}`} />
                <input name="promo_code" value={form.promo_code} onChange={handle} placeholder="z. B. NEUKUNDE30" className={`w-full h-12 pl-11 pr-4 rounded-lg border focus:border-[#B8892A] focus:ring-2 focus:ring-[#E0A72C]/30 outline-none transition font-mono font-bold tracking-widest uppercase ${form.promo_code ? 'bg-[#FEF3C7] border-[#E0A72C] text-[#0B1E44]' : 'bg-[#FAF7F2] border-slate-200 text-[#0B1E44]'}`} data-testid="contact-promo_code" />
              </div>
              {form.promo_code.toUpperCase() === 'NEUKUNDE30' && (
                <p className="mt-1.5 text-xs text-emerald-700 font-semibold inline-flex items-center gap-1" data-testid="promo-applied-hint"><CheckCircle2 className="w-3.5 h-3.5" /> 30 % Neukunden-Rabatt wird auf Ihren ersten Auftrag angerechnet.</p>
              )}
            </div>
          </div>

          <label className="mt-5 flex items-start gap-3 text-sm text-slate-600 cursor-pointer">
            <input type="checkbox" name="consent" checked={form.consent} onChange={handle} className="mt-1 w-4 h-4 accent-[#B8892A]" data-testid="contact-consent" />
            <span>Ich habe die <Link to="/datenschutz" className="text-[#1E3A8A] underline" data-testid="contact-privacy-link">Datenschutzerklärung</Link> gelesen und stimme der Verarbeitung meiner Daten zur Bearbeitung der Anfrage zu. *</span>
          </label>

          <button type="submit" disabled={status.state === 'loading'} className="mt-6 btn-gold inline-flex items-center gap-2 px-6 py-3.5 rounded-md text-[15px] disabled:opacity-70" data-testid="contact-submit">
            {status.state === 'loading' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {status.state === 'loading' ? (photos.some((p) => p.status === 'uploading') ? 'Fotos werden hochgeladen …' : 'Wird gesendet …') : 'Anfrage senden'}
          </button>

          {status.state === 'success' && (
            <div className="mt-5 flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-emerald-800" data-testid="contact-success">
              <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" /><span>{status.msg}</span>
            </div>
          )}
          {status.state === 'error' && (
            <div className="mt-5 flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg p-4 text-red-800" data-testid="contact-error">
              <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" /><span>{status.msg}</span>
            </div>
          )}
        </form>
      </div>
    </section>
  );
}

function Field({ label, ...props }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider font-semibold text-slate-600 mb-1.5">{label}</label>
      <input {...props} data-testid={`contact-${props.name}`} className="w-full h-12 px-4 rounded-lg bg-[#FAF7F2] border border-slate-200 focus:border-[#B8892A] focus:ring-2 focus:ring-[#E0A72C]/30 outline-none transition text-[#0B1E44]" />
    </div>
  );
}
